import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { LayoutDashboard, Clock, Download, GraduationCap, Users, BookOpen, Trophy, LibraryBig, FlaskConical } from 'lucide-react';
import { Button, Sidebar, SkipLink } from '@ludolio/shared-ui';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { DemoBanner } from '@/components/DemoBanner';
import { apiClient } from '@/services/api';

interface LayoutProps {
  children: React.ReactNode;
  teacherName: string;
}

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/units': 'Units',
  '/activity-library': 'Activity Library',
  '/students': 'Students',
  '/games': 'Games',
  '/achievements': 'Achievements',
  '/playing-time': 'Playing Time',
  '/install-times': 'Install Times',
  '/labs-reviews': 'Labs reviews',
  '/account': 'Account',
};

export function Layout({ children, teacherName: propTeacherName }: LayoutProps) {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const isDarkMode = useThemeStore((state) => state.isDarkMode);
  const toggleDarkMode = useThemeStore((state) => state.toggleDarkMode);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [labsAvailable, setLabsAvailable] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    requestAnimationFrame(() => mobileMenuButtonRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const menu = mobileMenuRef.current;
    const focusable = menu?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    focusable?.[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMobileMenu();
        return;
      }

      if (event.key !== 'Tab' || !focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeMobileMenu, mobileMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate({ to: '/' });
  };

  const handleAccountSettings = () => {
		navigate({ to: '/account' });
  };

  useEffect(() => {
    let cancelled = false;
    apiClient.client
      .post('/labs/availability', {})
      .then((response) => {
        if (!cancelled) setLabsAvailable(response.data?.data?.available === true);
      })
      .catch(() => {
        if (!cancelled) setLabsAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const baseMenuItems = [
    { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { label: 'Units', href: '/units', icon: GraduationCap },
    { label: 'Activity Library', href: '/activity-library', icon: LibraryBig },
    { label: 'Students', href: '/students', icon: Users },
    { label: 'Games', href: '/games', icon: BookOpen },
    { label: 'Achievements', href: '/achievements', icon: Trophy },
    { label: 'Playing Time', href: '/playing-time', icon: Clock },
    { label: 'Install Times', href: '/install-times', icon: Download },
    ...(labsAvailable ? [{ label: 'Labs reviews', href: '/labs-reviews', icon: FlaskConical }] : []),
  ];

  useEffect(() => {
    const pageTitle = PAGE_TITLES[currentPath]
      ?? (/^\/units\/[^/]+\/?$/.test(currentPath) ? 'Unit Details' : 'Instructor Dashboard');
    document.title = `${pageTitle} — Ludolio`;
  }, [currentPath]);

  const logo = (
    <div className="w-8 h-8 bg-[hsl(var(--color-primary))] rounded-lg flex items-center justify-center">
      <Trophy className="w-5 h-5 text-[hsl(var(--color-primary-foreground))]" />
    </div>
  );

  if (!user) {
    return null;
  }

  return (
    <div className="flex flex-col h-screen bg-[hsl(var(--color-background))]">
      <SkipLink />
      <DemoBanner />
      <div className="flex flex-1 min-h-0">
        <div className="hidden lg:block">
          <Sidebar
            logo={logo}
            title="Instructor"
            navItems={baseMenuItems}
            user={{
              name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || propTeacherName,
              email: user.email,
            }}
            activePath={currentPath}
            isDarkMode={isDarkMode}
            onToggleDarkMode={toggleDarkMode}
            onLogout={handleLogout}
            onAccountSettings={handleAccountSettings}
            LinkComponent={Link}
          />
        </div>

        <div
          ref={mobileMenuRef}
          className={`${mobileMenuOpen ? 'block' : 'hidden'} lg:hidden fixed inset-y-0 left-0 z-40`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <Sidebar
            id="instructor-mobile-navigation"
            logo={logo}
            title="Instructor"
            navItems={baseMenuItems}
            user={{
              name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || propTeacherName,
              email: user.email,
            }}
            activePath={currentPath}
            isDarkMode={isDarkMode}
            onToggleDarkMode={toggleDarkMode}
            onLogout={handleLogout}
            onAccountSettings={handleAccountSettings}
            LinkComponent={Link}
          />
        </div>

        <main
          id="main-content"
          className="flex-1 overflow-auto w-full"
          inert={mobileMenuOpen ? true : undefined}
        >
          <div className="lg:hidden sticky top-0 z-30 bg-[hsl(var(--color-card))] border-b border-border px-4 py-3 flex items-center justify-between">
            <Button
              ref={mobileMenuButtonRef}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              variant="ghost"
              size="icon"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="instructor-mobile-navigation"
            >
              <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[hsl(var(--color-primary))] rounded-lg flex items-center justify-center flex-shrink-0">
                <Trophy className="w-4 h-4 text-[hsl(var(--color-primary-foreground))]" />
              </div>
              <span className="font-bold">Ludolio</span>
            </div>
            <div className="w-10 h-10 bg-[hsl(var(--color-primary-action))] rounded-full flex items-center justify-center text-[hsl(var(--color-primary-action-foreground))] font-semibold text-sm flex-shrink-0">
              {user.firstName?.charAt(0) || propTeacherName.charAt(0)}
            </div>
          </div>

          <div className="container mx-auto p-4 sm:p-6 max-w-7xl">
            {children}
          </div>
        </main>
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
            aria-hidden="true"
            onClick={closeMobileMenu}
          />
        )}
      </div>
    </div>
  );
}

